import { useEventCreateMeetingTime } from "@crowdcoursing/api/openapi";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useToast } from "~hooks/useToast";
import { Route } from "~services/router/Route";
import { type MeetingTimeData } from "~ui/organisms/AddEventTimesModal/AddEventTimesModal.types";

interface EventSession {
  id: string;
  name: string;
  nextMeetingTime?: {
    capacity: number;
    id: string;
    beginMeetingTime: string;
    endMeetingTime: string;
  } | null;
}

export const useEventsSlideout = (events: EventSession[]) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [creatingMeetingTimes, setCreatingMeetingTimes] = useState(false);

  const toast = useToast();
  const { mutateAsync: createMeetingTime } = useEventCreateMeetingTime();

  // Filter and sort events
  const filteredEvents = useMemo(() => {
    let eventsToSort = events;

    // Filter by search query if provided
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      eventsToSort = events.filter((event) => {
        const name = (event.name || "").toLowerCase();
        return name.includes(query);
      });
    }

    // Sort events: upcoming times first (closest to now), then alphabetical
    return eventsToSort.sort((a, b) => {
      const aHasUpcoming = !!a.nextMeetingTime;
      const bHasUpcoming = !!b.nextMeetingTime;

      // Events with upcoming times go first
      if (aHasUpcoming && !bHasUpcoming) {
        return -1;
      }
      if (!aHasUpcoming && bHasUpcoming) {
        return 1;
      }

      // Both have upcoming times - sort by closest to now
      if (aHasUpcoming && bHasUpcoming && a.nextMeetingTime && b.nextMeetingTime) {
        const aTime = new Date(a.nextMeetingTime.beginMeetingTime).getTime();
        const bTime = new Date(b.nextMeetingTime.beginMeetingTime).getTime();
        return aTime - bTime;
      }

      // Both don't have upcoming times - sort alphabetically
      return (a.name || "").localeCompare(b.name || "");
    });
  }, [events, searchQuery]);

  const formatDateTime = (dateTimeString: string) => {
    const date = new Date(dateTimeString);
    return {
      date: date.toLocaleDateString("en-US", {
        day: "numeric",
        month: "short",
        weekday: "short",
      }),
      time: date.toLocaleTimeString("en-US", {
        hour: "numeric",
        hour12: true,
        minute: "2-digit",
      }),
    };
  };

  const formatDuration = (start: string, end: string) => {
    const startTime = new Date(start);
    const endTime = new Date(end);
    const durationMs = endTime.getTime() - startTime.getTime();
    const hours = Math.floor(durationMs / (1000 * 60 * 60));
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const handleCreateMeetingTimes = async (eventId: string, meetingTimes: MeetingTimeData[]) => {
    setCreatingMeetingTimes(true);

    let successCount = 0;
    let errorCount = 0;

    for (const meetingTime of meetingTimes) {
      try {
        await createMeetingTime({
          body: {
            beginMeetingTime: meetingTime.startTime.toISO() || meetingTime.startTime.toString(),
            endMeetingTime: meetingTime.endTime.toISO() || meetingTime.endTime.toString(),
            eventId,
            maximumSlots: meetingTime.capacity,
          },
          pathParams: { eventId },
        });
        successCount++;
      } catch (error) {
        console.error("Failed to create meeting time:", error);
        errorCount++;
      }
    }

    setCreatingMeetingTimes(false);

    if (successCount > 0) {
      const message =
        successCount === meetingTimes.length
          ? `Successfully created ${successCount} meeting times!`
          : `Created ${successCount} of ${meetingTimes.length} meeting times`;
      toast.success(message);
    }

    if (errorCount > 0) {
      toast.error(`Failed to create ${errorCount} meeting times`);
    }
  };

  const handleEventClick = (eventId: string) => {
    navigate(Route.eventView({ eventId }));
  };

  return {
    creatingMeetingTimes,
    filteredEvents,
    formatDateTime,
    formatDuration,
    handleCreateMeetingTimes,
    handleEventClick,
    searchQuery,
    setSearchQuery,
  };
};
